/**
 * ReachPicker — embeddable reach-profile radio group.
 *
 * Extracted from the standalone ReachStep so it can be used inside the
 * wizard's MarketContextStep card layout without the full-screen wrapper.
 */
import type { ReachProfile } from '../../../types';

interface ReachOption {
  value: ReachProfile;
  label: string;
  description: string;
  /** Where DataForSEO location codes are resolved from */
  locationHint: string;
  /** Tier required (Growth+ for global) */
  minTier: 'any' | 'growth';
}

export const REACH_OPTIONS: ReachOption[] = [
  {
    value: 'local',
    label: 'Local',
    description: 'Targets a single city or metro area. SERP scans use your primary location code. Competitor discovery is bounded to your city.',
    locationHint: 'Primary location required',
    minTier: 'any',
  },
  {
    value: 'national',
    label: 'National',
    description: 'Targets one or more countries without a city anchor. Good for e-commerce and national brands. SERP scans use country-level location.',
    locationHint: 'Country location used for scans',
    minTier: 'any',
  },
  {
    value: 'global',
    label: 'Global',
    description: 'Multi-country or international reach. Enables multi-market GEO sweeps and hreflang recommendations.',
    locationHint: 'Multiple countries tracked',
    minTier: 'growth',
  },
];

const TIER_RANK: Record<string, number> = { free: 0, starter: 1, tier_1: 1, growth: 2, tier_2: 2, scale: 3, tier_3: 3 };

function meetsMinTier(userTier: string, min: 'any' | 'growth'): boolean {
  if (min === 'any') return true;
  return (TIER_RANK[userTier] ?? 0) >= (TIER_RANK['growth'] ?? 2);
}

interface Props {
  value: ReachProfile | null;
  tier: string;
  onChange: (value: ReachProfile) => void;
}

export function ReachPicker({ value, tier, onChange }: Props) {
  return (
    <div className="space-y-2" data-testid="reach-picker">
      {REACH_OPTIONS.map((option) => {
        const isLocked = !meetsMinTier(tier, option.minTier);
        const isSelected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            data-testid={`reach-option-${option.value}`}
            disabled={isLocked}
            onClick={() => !isLocked && onChange(option.value)}
            className={`w-full text-left rounded-xl border-2 px-4 py-3.5 transition-colors ${
              isLocked
                ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                : isSelected
                ? 'border-brand-green bg-green-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            aria-pressed={isSelected}
          >
            <div className="flex items-center justify-between">
              <span className={`font-semibold text-sm ${isSelected ? 'text-brand-green' : 'text-brand-ink'}`}>
                {option.label}
              </span>
              <div className="flex items-center gap-2">
                {isLocked && (
                  <span className="text-[11px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-medium">
                    Growth+
                  </span>
                )}
                {isSelected && !isLocked && (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">{option.description}</p>
            <p className="text-[11px] text-gray-400 mt-1">{option.locationHint}</p>
          </button>
        );
      })}
    </div>
  );
}
